import { inject, Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { from, Observable, Subject, throwError } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ApiBaseService } from './api-base.service';
import { CommonService } from '../common.service';
import {
  PROJECT_FINANCE_INVOICE_MAX_SIZE_BYTES,
  ProjectFinanceAuthorizationOverridePayload,
  ProjectFinanceCostPayload,
  ProjectFinanceOtherCostPayload,
  ProjectInvoiceUpdatePayload
} from 'src/app/models/entities/project-finance';

const PROJECTS_BASE_URL = '/projects';
const PAYMENTS_REPORT_URL = `${PROJECTS_BASE_URL}/payments-report`;

const financeUrl = (projectId: number, path: string = ''): string => `${PROJECTS_BASE_URL}/${projectId}/finance${path}`;

@Injectable({
  providedIn: 'root'
})
export class ProjectFinanceService extends ApiBaseService {

  private readonly commonService = inject(CommonService);

  private readonly authorizationChangedSubject = new Subject<number>();
  readonly authorizationChanged$ = this.authorizationChangedSubject.asObservable();


  listInvoices(projectId: number): Observable<any> {
    return this.GET(this.getUrl(financeUrl(projectId, '/income')));
  }

  updateInvoice(projectId: number, projectInvoiceId: number, data: ProjectInvoiceUpdatePayload): Observable<any> {
    return this.PUT(this.getUrl(financeUrl(projectId, `/income/${projectInvoiceId}`)), data).pipe(
      tap(() => this.authorizationChangedSubject.next(projectId))
    );
  }

  uploadInvoiceAttachment(projectId: number, projectInvoiceId: number, file: File): Observable<any> {
    return this.uploadFile(financeUrl(projectId, `/income/${projectInvoiceId}/attachment`), file);
  }

  sendInvoice(projectId: number, projectInvoiceId: number): Observable<any> {
    return this.POST(this.getUrl(financeUrl(projectId, `/income/${projectInvoiceId}/send`)), {});
  }

  exportInvoicesExcel(projectId: number): Observable<any> {
    return this.GET(this.getUrl(financeUrl(projectId, '/income/excel')));
  }


  listAuthorizationHistory(projectId: number): Observable<any> {
    return this.GET(this.getUrl(financeUrl(projectId, '/authorization/history')));
  }

  overrideAuthorization(projectId: number, data: ProjectFinanceAuthorizationOverridePayload): Observable<any> {
    return this.POST(this.getUrl(financeUrl(projectId, '/authorization/override')), data).pipe(
      tap(() => this.authorizationChangedSubject.next(projectId))
    );
  }


  listTestingCosts(projectId: number): Observable<any> {
    return this.GET(this.getUrl(financeUrl(projectId, '/testing-costs')));
  }

  createTestingCost(projectId: number, data: ProjectFinanceCostPayload): Observable<any> {
    return this.POST(this.getUrl(financeUrl(projectId, '/testing-costs')), data);
  }

  updateTestingCost(projectId: number, projectFinanceCostId: number, data: ProjectFinanceCostPayload): Observable<any> {
    return this.PUT(this.getUrl(financeUrl(projectId, `/testing-costs/${projectFinanceCostId}`)), data);
  }

  deleteTestingCost(projectId: number, projectFinanceCostId: number): Observable<any> {
    return this.DELETE(this.getUrl(financeUrl(projectId, `/testing-costs/${projectFinanceCostId}`)));
  }


  listOtherCosts(projectId: number): Observable<any> {
    return this.GET(this.getUrl(financeUrl(projectId, '/other-costs')));
  }

  createOtherCost(projectId: number, data: ProjectFinanceOtherCostPayload): Observable<any> {
    return this.POST(this.getUrl(financeUrl(projectId, '/other-costs')), data);
  }

  updateOtherCost(projectId: number, projectFinanceOtherCostId: number, data: ProjectFinanceOtherCostPayload): Observable<any> {
    return this.PUT(this.getUrl(financeUrl(projectId, `/other-costs/${projectFinanceOtherCostId}`)), data);
  }

  deleteOtherCost(projectId: number, projectFinanceOtherCostId: number): Observable<any> {
    return this.DELETE(this.getUrl(financeUrl(projectId, `/other-costs/${projectFinanceOtherCostId}`)));
  }

  uploadOtherCostAttachment(projectId: number, projectFinanceOtherCostId: number, file: File): Observable<any> {
    return this.uploadFile(financeUrl(projectId, `/other-costs/${projectFinanceOtherCostId}/attachment`), file);
  }


  getBalance(projectId: number): Observable<any> {
    return this.GET(this.getUrl(financeUrl(projectId, '/balance')));
  }

  listProjectPaymentsReport(date: string): Observable<any> {
    return this.GET(this.getUrl(PAYMENTS_REPORT_URL), new HttpParams().set('date', date));
  }

  exportProjectPaymentsReportExcel(date: string): Observable<any> {
    return this.GET(this.getUrl(`${PAYMENTS_REPORT_URL}/excel`), new HttpParams().set('date', date));
  }

  attachmentPublicUrl(attachmentUrl: string): Observable<string> {
    return this.GET(attachmentUrl).pipe(
      map((response: any) => this.readResponseKey<any>(response, 'attachment')?.publicUrl ?? '')
    );
  }



  readResponseKey<T>(response: any, key: string): T | undefined {
    return response?.data?.[key] as T | undefined;
  }

  errorMessage(error: any, fallback: string): string {
    return error?.error?.status?.description || error?.error?.status?.message || error?.error?.message || fallback;
  }

  validateInvoiceFile(file: File): string | null {
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      return 'Only PDF files are allowed';
    }
    if (file.size > PROJECT_FINANCE_INVOICE_MAX_SIZE_BYTES) {
      return 'The file exceeds the 10 MB limit';
    }
    return null;
  }

  private uploadFile(path: string, file: File): Observable<any> {
    if (!this.commonService.isWebCryptoAvailable()) {
      return throwError(() => ({
        error: { message: 'This browser cannot verify file integrity. Please use an updated browser.' }
      }));
    }

    return from(this.commonService.computeSha256Hex(file)).pipe(
      switchMap(hash => {
        const formData = new FormData();
        formData.append('file', file, file.name);
        const params = new HttpParams()
          .set('hash', hash)
          .set('hashAlg', environment.fileHashAlgorithm);
        return this.POSTMULTIPART(this.getUrl(path), formData, params);
      })
    );
  }
}
